import { EXCLUDE_DATA_ACCESS_POLICIES_BY_PRODUCT_AREA_OBJ } from 'admin/company/constants/rolesConstants';
import { RBAC_PRODUCT_AREAS_WITH_DEFAULT } from 'common/constants/rbacProductAreas';

type RBACProductAreasKeys = keyof typeof RBAC_PRODUCT_AREAS_WITH_DEFAULT;
export type RBACProductAreasValues = (typeof RBAC_PRODUCT_AREAS_WITH_DEFAULT)[RBACProductAreasKeys];
interface ScopeOption {
  scope_name: string;
  specific_groups?: number[];
}
export interface DataAccessPolicies {
  feedback: ScopeOption;
  qa: ScopeOption;
  coaching: ScopeOption;
  administrative: ScopeOption;
  default: ScopeOption;
  social: ScopeOption;
}

interface Option {
  value: string;
  label: string;
}

export const toValidatedList = (isEnabled, linkItem) => {
  return isEnabled ? [linkItem] : [];
};

export const validateSpecificGroupsSelection = (dataAccessPolicies: DataAccessPolicies) => {
  const emptySpecificGroupsProductAreas: string[] = [];

  Object.keys(dataAccessPolicies).forEach(productArea => {
    const productAreaPolicy = dataAccessPolicies[productArea];
    if (productAreaPolicy.scope_name === 'specific_groups') {
      if (productAreaPolicy.specific_groups.length === 0) {
        emptySpecificGroupsProductAreas.push(productArea);
      }
    }
  });

  return {
    isValid: emptySpecificGroupsProductAreas.length === 0,
    emptySpecificGroupsProductAreas,
  };
};

export const COACH = 'coach';
export const COACHEE = 'participant';
export const VIEW_ONE_ON_ONES = 'viewer';
export const SAVE_FOR_ONE_ON_ONES = 'save_for_1_1s';
export const VIEW_ONE_ON_ONES_REPORTING = 'view_1_1s_reporting';

export const RBAC_ONE_ON_ONES_TYPE = {
  COACH,
  COACHEE,
  VIEW_ONE_ON_ONES,
  SAVE_FOR_ONE_ON_ONES,
  VIEW_ONE_ON_ONES_REPORTING,
};

export const filteredDAPOptionsByExcludedPolicies = (
  productAreaName: string,
  formattedOptions: Option[]
) => {
  const filteredOptions = formattedOptions.filter(option => {
    const isExcluded = EXCLUDE_DATA_ACCESS_POLICIES_BY_PRODUCT_AREA_OBJ[productAreaName]?.includes(
      option.value
    );
    return !isExcluded;
  });
  return filteredOptions;
};

export const showLoader = (elmName: string) => {
  const elm = document.getElementById(elmName);
  if (elm) {
    elm.classList.remove('hidden');
  }
};

export const hideLoader = (elmName: string) => {
  const elm = document.getElementById(elmName);
  if (elm) {
    elm.classList.add('hidden');
  }
};

const defaultScopeRequirementForScopes = {
  self: ['self'],
  company: ['company', 'self', 'own_group', 'own_team', 'specific_groups'],
  own_group: ['own_group', 'self'],
  own_team: ['own_team', 'self'],
};

export const validateAppealsScope = (dataAccessPolicies: DataAccessPolicies) => {
  const qaScope = dataAccessPolicies[RBAC_PRODUCT_AREAS_WITH_DEFAULT.QA].scope_name;
  const appealScope = dataAccessPolicies[RBAC_PRODUCT_AREAS_WITH_DEFAULT.QA_APPEAL].scope_name;

  return !defaultScopeRequirementForScopes[qaScope].includes(appealScope);
};

export const validateDataScopePolicies = (
  dataAccessPolicies: DataAccessPolicies,
  appealFeatureOn = false,
  feedbackEnabled = false
) => {
  const selectedDefaultScope = dataAccessPolicies.default.scope_name;
  const allowedScopesForProductAreas = defaultScopeRequirementForScopes[selectedDefaultScope];

  // except for default and social, on DataAccessPolicies, the scope_name should be one of the allowedScopesForProductAreas
  const invalidDataScopeProductAreas = Object.keys(dataAccessPolicies).filter(productArea => {
    if (productArea == RBAC_PRODUCT_AREAS_WITH_DEFAULT.QA_APPEAL && !appealFeatureOn) {
      return false;
    }
    if (productArea == RBAC_PRODUCT_AREAS_WITH_DEFAULT.FEEDBACK && !feedbackEnabled) {
      return false;
    }
    if (
      productArea !== RBAC_PRODUCT_AREAS_WITH_DEFAULT.DEFAULT &&
      productArea !== RBAC_PRODUCT_AREAS_WITH_DEFAULT.SOCIAL
    ) {
      const scopeName = dataAccessPolicies[productArea].scope_name;
      return !allowedScopesForProductAreas.includes(scopeName);
    }
    return false;
  });

  return {
    hasValidDataScopes: invalidDataScopeProductAreas.length === 0,
    invalidDataScopeProductAreas,
  };
};

export const getScopeNameByProductArea = (
  productArea: RBACProductAreasValues,
  dataAccessPolicies: DataAccessPolicies
) => {
  const productAreaPolicy = dataAccessPolicies[productArea] || {};
  return productAreaPolicy.scope_name;
};

export const getTeamsOrGroupsDropdownHidden = (
  productArea: RBACProductAreasValues,
  dataAccessPolicies: DataAccessPolicies
) => {
  const scopeName = getScopeNameByProductArea(productArea, dataAccessPolicies);
  return scopeName === 'self';
};

export const getCompanyDropdownHidden = (
  productArea: RBACProductAreasValues,
  dataAccessPolicies: DataAccessPolicies
) => {
  const scopeName = getScopeNameByProductArea(productArea, dataAccessPolicies);
  return scopeName !== 'company';
};
